import { useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, ShieldCheck, Clock, Upload, X, Paperclip } from "lucide-react";
import { submitAssessment, uploadFile } from "@/lib/api";
import { SERVICES } from "@/data/services";
import SEO from "@/components/SEO";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const PROPERTY_TYPES = ["Single-family Home", "Multi-family / Apartment", "Commercial Office", "School", "Healthcare", "Industrial", "Other"];
const BYTES_PER_KB = 1024;

export default function Schedule() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    property_type: "",
    service_interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{id, filename, size}]
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (attachments.length + files.length > 5) {
      toast.error("Maximum 5 attachments per request.");
      return;
    }
    setUploading(true);
    for (const f of files) {
      try {
        const rec = await uploadFile(f);
        setAttachments((prev) => [...prev, rec]);
      } catch (err) {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.property_type || !form.service_interest) {
      toast.error("Please select a property type and service.");
      return;
    }
    setLoading(true);
    try {
      await submitAssessment({ ...form, attachment_ids: attachments.map((a) => a.id) });
      toast.success("Request received. A consultant will contact you within one business day.");
      setSubmitted(true);
    } catch (err) {
      toast.error("Could not submit. Please call us at 956.217.2604.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="schedule-page">
      <SEO
        title="Schedule an Environmental Assessment in Texas"
        description="Request an independent environmental assessment. Mold, indoor air quality, moisture, water damage, asbestos. Most assessments scheduled within 48–72 hours."
        path="/schedule"
      />
      <section className="bg-[#0F172A] text-white py-24">
        <div className="container-x max-w-5xl">
          <div className="overline text-[#F59E0B] mb-5">Schedule</div>
          <h1 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Request an independent environmental assessment.
          </h1>
          <p className="mt-8 text-lg text-white/70 max-w-2xl leading-relaxed">
            Tell us about the property and the concern. A Hurtler consultant will respond within
            one business day with availability and a clear next step.
          </p>
          <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-3xl">
            {[
              { icon: ShieldCheck, t: "Independent" },
              { icon: Clock, t: "1-day response" },
              { icon: CalendarCheck, t: "48–72hr scheduling" },
            ].map((p) => (
              <div key={p.t} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-center gap-3">
                <p.icon className="w-5 h-5 text-[#F59E0B]" />
                <span className="text-sm">{p.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-x max-w-3xl">
          {submitted ? (
            <div data-testid="schedule-success" className="card-soft p-12 text-center">
              <CalendarCheck className="w-12 h-12 text-[#7C3AED] mx-auto" />
              <h2 className="font-display text-3xl font-bold text-[#0F172A] mt-6">Request received.</h2>
              <p className="text-slate-600 mt-4 max-w-md mx-auto leading-relaxed">
                A Hurtler consultant will contact you within one business day to confirm details and
                schedule your assessment.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="schedule-form" className="card-soft p-8 md:p-10 space-y-6">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-semibold text-[#0F172A]">Full name</label>
                  <input
                    data-testid="schedule-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#7C3AED]"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#0F172A]">Email</label>
                  <input
                    data-testid="schedule-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#7C3AED]"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#0F172A]">Phone</label>
                  <input
                    data-testid="schedule-phone-input"
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-2 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#7C3AED]"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#0F172A]">City</label>
                  <input
                    data-testid="schedule-city-input"
                    required
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="mt-2 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#7C3AED]"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#0F172A]">Property type</label>
                  <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                    <SelectTrigger data-testid="schedule-property-type" className="mt-2 h-12 rounded-lg">
                      <SelectValue placeholder="Select a property type" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROPERTY_TYPES.map((p) => (
                        <SelectItem key={p} value={p} data-testid={`property-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#0F172A]">Service of interest</label>
                  <Select value={form.service_interest} onValueChange={(v) => setForm({ ...form, service_interest: v })}>
                    <SelectTrigger data-testid="schedule-service-interest" className="mt-2 h-12 rounded-lg">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s.slug} value={s.title} data-testid={`service-option-${s.slug}`}>{s.title}</SelectItem>
                      ))}
                      <SelectItem value="Not sure — please advise" data-testid="service-option-not-sure">Not sure — please advise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-[#0F172A]">Tell us about the concern</label>
                <textarea
                  data-testid="schedule-message-input"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Symptoms, history, timing, property age, anything that helps us prepare."
                  className="mt-2 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#7C3AED]"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-[#0F172A]">
                  Photos or documents <span className="text-slate-400 font-normal">(optional, up to 5 • images or PDF)</span>
                </label>
                <div className="mt-2 rounded-lg border-2 border-dashed border-slate-200 p-5 hover:border-[#7C3AED] transition-colors">
                  <label htmlFor="schedule-files" className="flex items-center justify-center gap-3 cursor-pointer text-slate-600 hover:text-[#7C3AED]">
                    <Upload className="w-5 h-5" />
                    <span className="text-sm font-medium">
                      {uploading ? "Uploading…" : "Click to add photos of the concern"}
                    </span>
                  </label>
                  <input
                    id="schedule-files"
                    data-testid="schedule-files-input"
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    onChange={handleFiles}
                    disabled={uploading || attachments.length >= 5}
                    className="hidden"
                  />
                </div>
                {attachments.length > 0 && (
                  <ul data-testid="schedule-attachments-list" className="mt-3 space-y-2">
                    {attachments.map((a) => (
                      <li key={a.id} className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg bg-[#F8FAFC] border border-slate-100">
                        <div className="flex items-center gap-3 min-w-0">
                          <Paperclip className="w-4 h-4 text-[#7C3AED] shrink-0" />
                          <span className="text-sm text-slate-700 truncate">{a.filename}</span>
                          <span className="text-xs text-slate-400 shrink-0">{Math.round(a.size / BYTES_PER_KB)} KB</span>
                        </div>
                        <button
                          type="button"
                          data-testid={`remove-attachment-${a.id}`}
                          onClick={() => removeAttachment(a.id)}
                          className="text-slate-400 hover:text-[#0F172A] shrink-0"
                          aria-label="Remove attachment"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button
                data-testid="schedule-submit-button"
                disabled={loading}
                type="submit"
                className="btn-primary w-full justify-center"
              >
                {loading ? "Submitting…" : "Request Assessment"}
              </button>
              <p className="text-xs text-slate-500 text-center">
                We respect your privacy. Your information is used only to coordinate your assessment.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
